import { useEffect, useMemo, useRef, useState } from 'react';
import title1 from '../../assets/editorial/titles/01-querida-kitty.svg';
import title2 from '../../assets/editorial/titles/02-a-convocacao.svg';
import title3 from '../../assets/editorial/titles/03-so-por-serem-judeus.svg';
import title4 from '../../assets/editorial/titles/04-um-pedaco-do-ceu.svg';
import title5 from '../../assets/editorial/titles/05-escrever-para-respirar.svg';
import title6 from '../../assets/editorial/titles/06-oito-coracoes.svg';
import title7 from '../../assets/editorial/titles/07-apesar-de-tudo.svg';
import title8 from '../../assets/editorial/titles/08-as-duas-annes.svg';
import EditorialPageFrame from '../../components/EditorialPageFrame';
import ProgressCircle from '../../components/ProgressCircle';
import { chapterByKey, spokenSteps } from '../../editorial/model';
import { flattenFragments } from '../../editorial/parseConteudos';
import { useFontsReady } from '../../editorial/useFontsReady';
import useChapter from '../useChapter';
import { mountPhraseAnimation } from '../utils/mountPhraseAnimation';
import { LETTER_DURATION, phraseAnimationPlan, prefersReducedMotion } from '../utils/phraseAnimationPlan';

const afterwordWait = (text) => {
  const plan = phraseAnimationPlan(text, { reduced: prefersReducedMotion(), preset: 'blurry' });
  if (plan.mode === 'instant' || !plan.letters.length) return 0;
  const last = plan.letters[plan.letters.length - 1];
  return Math.round((last.delay + LETTER_DURATION) * 1000);
};

const plain = (text) => text.replace(/\n/g, ' ');

const TITLE_ART = {
  chapter1: title1,
  chapter2: title2,
  chapter3: title3,
  chapter4: title4,
  chapter5: title5,
  chapter6: title6,
  chapter7: title7,
  chapter8: title8,
};

const ChapterSession = ({ chapter, shared }) => {
  const fragments = useMemo(() => flattenFragments(chapter), [chapter]);
  const [shown, setShown] = useState(1);
  const [nodes, setNodes] = useState([]);
  const [showAfter, setShowAfter] = useState(false);
  const [afterNode, setAfterNode] = useState(null);
  const [showNext, setShowNext] = useState(false);
  const finishTimer = useRef(null);

  const triggers = {
    finish: () => {
      setShowAfter(true);
      const wait = chapter.afterword ? afterwordWait(chapter.afterword.text) : 0;
      finishTimer.current = setTimeout(() => setShowNext(true), wait);
    },
  };
  fragments.forEach((_, index) => {
    if (index < fragments.length - 1) {
      triggers[`reveal-${index + 1}`] = () => setShown(index + 2);
    }
  });

  useChapter({
    ...shared,
    phrasesToRead: spokenSteps(chapter),
    triggers,
    triggerDelay: 0,
  });

  useEffect(() => () => clearTimeout(finishTimer.current), []);

  useEffect(() => {
    if (!showAfter || !chapter.afterword) return undefined;
    mountPhraseAnimation(
      `${chapter.key}-after`,
      chapter.afterword.text,
      setAfterNode,
      'blurry',
    );
    return undefined;
  }, [showAfter, chapter.key, chapter.afterword]);

  useEffect(() => {
    const fragment = fragments[shown - 1];
    if (!fragment) return undefined;
    mountPhraseAnimation(
      `${chapter.key}-${fragment.index}`,
      fragment.visual,
      (node) => {
        setNodes((current) => {
          const next = current.slice();
          next[fragment.index] = node;
          return next;
        });
      },
      fragment.preset,
    );
    return undefined;
  }, [shown, chapter.key, fragments]);

  const latest = fragments[shown - 1];
  const highlightAt = chapter.blocks.findIndex((item) => item.role === 'highlight');

  return (
    <EditorialPageFrame meta={chapter.meta} layout={chapter.key}>
      <h1 className="editorial-title">
        <img src={TITLE_ART[chapter.key]} alt={plain(chapter.title)} />
      </h1>
      <div className="sr-only" aria-live="polite">{latest ? plain(latest.visual) : ''}</div>
      {chapter.blocks.map((block, blockIndex) => {
        const visible = fragments.filter((fragment) => fragment.blockIndex === blockIndex && fragment.index < shown);
        if (!visible.length) return null;
        const blockClass = block.role === 'highlight'
          ? 'editorial-highlight'
          : highlightAt !== -1 && blockIndex > highlightAt
            ? 'editorial-body editorial-body--after'
            : 'editorial-body';
        return (
          <div key={`${chapter.key}-block-${blockIndex}`} className={blockClass}>
            {visible.map((fragment) => (
              <p
                key={`${chapter.key}-${fragment.index}`}
                className={fragment.flow ? 'editorial-fragment editorial-fragment--flow' : 'editorial-fragment'}
                aria-hidden="true"
              >
                {nodes[fragment.index]}
              </p>
            ))}
          </div>
        );
      })}
      {showAfter && chapter.afterword && (
        <div className="editorial-after">
          <p>{afterNode}</p>
        </div>
      )}
      {shared.isTransitioning && <div className="page-transition" />}
      <ProgressCircle
        visible={showNext}
        label="Próximo capítulo"
        onClick={shared.onNextPage}
      />
    </EditorialPageFrame>
  );
};

const EditorialChapter = (props) => {
  const fontsReady = useFontsReady();
  const chapter = chapterByKey(props.pageName);

  if (!chapter) return null;
  if (!fontsReady) return <EditorialPageFrame meta={chapter.meta} layout={chapter.key} />;

  return <ChapterSession chapter={chapter} shared={props} />;
};

export default EditorialChapter;
